import React from 'react';
import { getCommunityMember } from '@/api/communities';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react/dist/ssr/PencilSimple';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

import { EditMemberModal } from '../components/edit-member-modal';

export function CommunityMemberPage(): React.JSX.Element {
  const { memberId } = useParams<{ memberId: string }>();
  const { community } = useUser();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['member', memberId],
    queryFn: async () => {
      const response = await getCommunityMember(community?.id!, memberId!);
      return response.member;
    },
  });

  const handleOnSuccess = React.useCallback(
    (user: User) => {
      toast.success(`Success!`, {
        description: `${user.first_name} ${user.last_name} has been updated successfully`,
        position: 'top-right',
        style: {
          background: '#107E4A',
          color: '#fff',
          borderColor: '#107E4A',
        },
      });
      refetch();
    },
    [refetch]
  );

  const dialog = useDialog();
  return (
    <React.Fragment>
      <Box sx={{ mb: 2 }}>
        <Button
          aria-label="Back to Members"
          onClick={() => {
            navigate(paths.settings.community.members.index);
          }}
          startIcon={<ArrowLeftIcon size={24} />}
          variant="text"
        >
          Back to Members
        </Button>
      </Box>

      {isLoading ? <div>Loading...</div> : null}
      {error ? <div>Error: {error.message}</div> : null}

      <Stack spacing={4}>
        <Grid container spacing={4}>
          {data ? (
            <Grid lg={12} xs={12}>
              <Stack spacing={4}>
                <Card>
                  <CardHeader
                    action={
                      <IconButton
                        onClick={() => {
                          dialog.handleOpen();
                        }}
                      >
                        <PencilSimpleIcon />
                      </IconButton>
                    }
                    avatar={
                      <Avatar>
                        <UserIcon fontSize="var(--Icon-fontSize)" />
                      </Avatar>
                    }
                    title="User Details"
                  />
                  <PropertyList
                    divider={<Divider />}
                    orientation="vertical"
                    sx={{ '--PropertyItem-padding': '12px 24px' }}
                  >
                    {(
                      [
                        { key: 'ID', value: <Chip label={data.id} size="small" variant="soft" /> },
                        { key: 'Name', value: `${data.first_name} ${data.last_name}` },
                        { key: 'Email', value: data.email },
                        { key: 'Phone', value: data.phone },
                      ] satisfies { key: string; value: React.ReactNode }[]
                    ).map(
                      (item): React.JSX.Element => (
                        <PropertyItem key={item.key} name={item.key} value={item.value} />
                      )
                    )}
                  </PropertyList>
                </Card>
              </Stack>
            </Grid>
          ) : null}
        </Grid>
      </Stack>
      <EditMemberModal
        communityId={community?.id ?? ''}
        onClose={dialog.handleClose}
        onSuccess={handleOnSuccess}
        open={dialog.open}
        user={data ?? ({} as User)}
      />
    </React.Fragment>
  );
}
